import { Injectable, inject } from '@angular/core';
import { AppStateService } from '@siza-staff-care/shared/app-state';
import {
	ItemsFolderWithItemsLookupDto,
	LookupDto,
	Name_MiscellaneousFolder,
	StaffItemsLookupDto,
} from '@siza-staff-care/shared/data';
import { PseudoFolderColors, hexToJadeColor } from '@siza-staff-care/shared/utils';
import { keyBy, orderBy } from 'lodash-es';

export type Lookups = {
	events: StaffItemsLookupDto[];
	skills: StaffItemsLookupDto[];
	staffExtras: StaffItemsLookupDto[];
	trainings: StaffItemsLookupDto[];
};

type FolderEntry = ItemsFolderWithItemsLookupDto & { isDisabled?: boolean };

type HierarchyDto = LookupDto & { parent: string; isDisabled?: boolean };

@Injectable({
	providedIn: 'root',
})
export class FolderHierarchyBuilderService {
	private appState = inject(AppStateService);

	buildFoldersHierarchy(
		showPastFutureItems: boolean,
		lookups: Lookups,
		folders: ItemsFolderWithItemsLookupDto[],
	): { staffItemsByOid: { [oid: string]: StaffItemsLookupDto }; hierarchy: HierarchyDto[] } {
		const useFolders = this.appState.settings.enableFolders;

		let events = lookups.events;
		let skills = lookups.skills;
		let staffExtras = lookups.staffExtras;
		let trainings = lookups.trainings;

		if (!showPastFutureItems) {
			events = events.filter((e) => !e.outsideValid);
			skills = skills.filter((e) => !e.outsideValid);
			staffExtras = staffExtras.filter((e) => !e.outsideValid);
			trainings = trainings.filter((e) => !e.outsideValid);
		}

		// Build a dictionary of all staff items, keyed by OID.
		const allStaffItems = [...events, ...skills, ...staffExtras, ...trainings];
		const staffItemsByOid: { [oid: string]: StaffItemsLookupDto } = keyBy(allStaffItems, (i) => i.oid);

		if (useFolders) {
			// Move the Misc folder to the end.
			const miscFolder = folders.find((f) => f.desc === Name_MiscellaneousFolder);
			if (!miscFolder) throw new Error('Miscellaneous folder not found');

			const ndx = folders.indexOf(miscFolder);
			folders.splice(ndx, 1);
			folders.push(miscFolder);
		} else {
			folders = [
				{
					oid: String(1),
					desc: 'Events',
					includeAllEvents: true,
					color: hexToJadeColor(PseudoFolderColors.EVENTS_HEX),
				} as ItemsFolderWithItemsLookupDto,
				{
					oid: String(2),
					desc: 'Skills/competencies',
					includeAllSkillCompetencies: true,
					color: hexToJadeColor(PseudoFolderColors.SKILLS_HEX),
				} as ItemsFolderWithItemsLookupDto,
				{
					oid: String(3),
					desc: 'Staff extras',
					includeAllStaffExtras: true,
					color: hexToJadeColor(PseudoFolderColors.EXTRAS_HEX),
				} as ItemsFolderWithItemsLookupDto,
				{
					oid: String(4),
					desc: 'Trainings',
					includeAllTrainings: true,
					color: hexToJadeColor(PseudoFolderColors.TRAININGS_HEX),
				} as ItemsFolderWithItemsLookupDto,
			];
		}

		const result = [] as HierarchyDto[];
		const addItems = (items, parent) => {
			items.forEach((i) => {
				// Add the item if not already included.
				if (parent.allItems.indexOf(i.oid) < 0) {
					parent.allItems.push(i.oid);
				}
			});
		};

		// Build a combined lookup by OID of all the admin items.
		const combined = keyBy([...events, ...skills, ...staffExtras, ...trainings], 'oid');

		// Build a collection of folders and their items - each item references a parent being the OID of the folder that it's in.
		folders.forEach((f) => {
			if (f.hide) {
				return;
			}

			const allItems = f.allItems || [];

			const folderClone: FolderEntry = {
				...f,
			};

			if (f.includeAllEvents) {
				addItems(events, f);
			}
			if (f.includeAllSkillCompetencies) {
				addItems(skills, f);
			}
			if (f.includeAllStaffExtras) {
				addItems(staffExtras, f);
			}
			if (f.includeAllTrainings) {
				addItems(trainings, f);
			}

			let children = [] as LookupDto[];
			allItems.forEach((itemOid) => {
				const item = combined[itemOid];
				if (item) {
					const clone = {
						...item,
						parent: f.oid,
					};

					children.push(clone);
				}
			});

			// Sort the children
			children = orderBy(children, 'desc');
			children.forEach((c) => {
				result.push(c as HierarchyDto);
			});

			if (children.length) {
				// Don't add empty folders.
				result.push({
					oid: folderClone.oid,
					code: folderClone.code,
					desc: folderClone.desc,
					parent: '',
				} as HierarchyDto);
			} else {
				// If a folder has no children, disable it. This prevents it from appearing as a selected item which would erroneously
				// appear to have selections when there are none.
				folderClone.isDisabled = true;
			}
		});

		return {
			staffItemsByOid,
			hierarchy: result,
		};
	}
}
